import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ReferralCard } from '../components/ReferralCard';
import { ReferralLeaderboard } from '../components/ReferralLeaderboard';
import { ChatInterface } from '../components/ChatInterface';
import { CreditsWidget } from '../components/CreditsPanel';
import { DeployedApps } from '../components/DeployedApps';
import { DeployModal } from '../components/DeployModal';
import MonitoringPanel from '../components/MonitoringPanel';
import ProjectIDE from '../components/ProjectIDE';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Clock, Trash2, Loader2, Sparkles, Gift, Trophy, Search, ChevronDown, Check, Coins, Rocket, Activity, Code } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  
  const [projects, setProjects] = useState([]);
  const [recentTasks, setRecentTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', type: 'fullstack', description: '' });
  const [creating, setCreating] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeTab, setActiveTab] = useState('chat');
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const [sendingPrompt, setSendingPrompt] = useState(false);
  const [surprisePrompt, setSurprisePrompt] = useState('');
  const [templatePrompt, setTemplatePrompt] = useState('');
  const [templateProcessed, setTemplateProcessed] = useState(false);
  const [showIDE, setShowIDE] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchData();
  }, [user, navigate]);

  // Handle template prompt from location state
  useEffect(() => {
    if (location.state?.prompt && !templateProcessed) {
      setTemplatePrompt(location.state.prompt);
      setTemplateProcessed(true);
      // Clear the state to prevent re-processing on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, templateProcessed]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProjectDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, tasksRes] = await Promise.all([
        axios.get(`${API}/projects`),
        axios.get(`${API}/tasks/recent`)
      ]);
      setProjects(projectsRes.data);
      setRecentTasks(tasksRes.data);
      if (projectsRes.data.length > 0 && !selectedProject) {
        setSelectedProject(projectsRes.data[0]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await axios.post(`${API}/projects`, newProject);
      setProjects([response.data, ...projects]);
      setSelectedProject(response.data);
      setShowNewProject(false);
      setShowProjectDropdown(false);
      setNewProject({ name: '', type: 'fullstack', description: '' });
      toast.success('Proyecto creado');
    } catch (error) {
      toast.error('Error al crear');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteProject = async (projectId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      const newProjects = projects.filter(p => p.id !== projectId);
      setProjects(newProjects);
      if (selectedProject?.id === projectId) setSelectedProject(newProjects[0] || null);
      toast.success('Eliminado');
    } catch (error) {
      toast.error('Error');
    }
  };

  const handleSendMessage = async (prompt, model) => {
    if (!selectedProject) { toast.error('Selecciona un proyecto'); return null; }
    if (user.credits < 1) { toast.error('Sin créditos'); navigate('/pricing'); return null; }
    setSendingPrompt(true);
    try {
      const response = await axios.post(`${API}/tasks`, { project_id: selectedProject.id, prompt, model });
      setRecentTasks([response.data, ...recentTasks.slice(0, 9)]);
      await refreshUser();
      toast.success('Respuesta generada');
      return response.data;
    } catch (error) {
      toast.error('Error');
      return null;
    } finally {
      setSendingPrompt(false);
    }
  };

  const handleSurprise = async () => {
    try {
      const response = await axios.get(`${API}/surprise-prompt`);
      setSurprisePrompt(response.data.prompt);
    } catch (error) { console.error(error); }
  };

  const getProjectColor = (type) => {
    if (type === 'fullstack') return 'bg-cyan-500';
    if (type === 'mobile') return 'bg-orange-500';
    return 'bg-green-500';
  };

  const filteredProjects = projects.filter(p => p.name.toLowerCase().includes(projectSearch.toLowerCase()));

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#FF6B00]" /></div>;

  return (
    <div className="app-container min-h-screen">
      <Navbar />
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Project Selector */}
          <div className="flex justify-center mb-8" ref={dropdownRef}>
            <div className="relative">
              <button onClick={() => setShowProjectDropdown(!showProjectDropdown)} className="flex items-center gap-3 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 hover:border-white/20" data-testid="project-selector-btn">
                {selectedProject ? (<><div className={`w-3 h-3 rounded-full ${getProjectColor(selectedProject.type)}`} /><span className="text-white font-medium">{selectedProject.name}</span></>) : <span className="text-gray-400">Seleccionar proyecto</span>}
                <ChevronDown className={`w-4 h-4 text-gray-400 ${showProjectDropdown ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {showProjectDropdown && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-80 rounded-2xl bg-[#0A0A0A] border border-white/10 shadow-2xl overflow-hidden z-50">
                    <div className="p-3 border-b border-white/10">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <input type="text" value={projectSearch} onChange={(e) => setProjectSearch(e.target.value)} placeholder="Buscar" className="w-full pl-9 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none" />
                      </div>
                    </div>
                    {selectedProject && (
                      <div className="p-3 border-b border-white/10">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-4 h-4 rounded-full ${getProjectColor(selectedProject.type)}`} />
                            <div><p className="text-white font-medium">{selectedProject.name}</p><p className="text-xs text-gray-500">Propietario</p></div>
                          </div>
                          <Check className="w-5 h-5 text-green-400" />
                        </div>
                      </div>
                    )}
                    <div className="max-h-48 overflow-y-auto">
                      {filteredProjects.filter(p => p.id !== selectedProject?.id).map((project) => (
                        <button key={project.id} onClick={() => { setSelectedProject(project); setShowProjectDropdown(false); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 group">
                          <div className={`w-4 h-4 rounded-full ${getProjectColor(project.type)}`} />
                          <div className="flex-1 text-left"><p className="text-gray-300 font-medium">{project.name}</p></div>
                          <button onClick={(e) => handleDeleteProject(project.id, e)} className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/20 text-gray-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                        </button>
                      ))}
                    </div>
                    <div className="p-3 border-t border-white/10">
                      {showNewProject ? (
                        <form onSubmit={handleCreateProject} className="space-y-3">
                          <input type="text" value={newProject.name} onChange={(e) => setNewProject({ ...newProject, name: e.target.value })} placeholder="Nombre" required className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white" autoFocus />
                          <select value={newProject.type} onChange={(e) => setNewProject({ ...newProject, type: e.target.value })} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white">
                            <option value="fullstack">Full Stack</option>
                            <option value="mobile">Móvil</option>
                            <option value="landing">Landing</option>
                          </select>
                          <div className="flex gap-2">
                            <button type="button" onClick={() => setShowNewProject(false)} className="flex-1 py-2 rounded-lg border border-white/10 text-gray-400 text-sm">Cancelar</button>
                            <button type="submit" disabled={creating} className="flex-1 py-2 rounded-lg bg-[#FF6B00] text-white text-sm">{creating ? '...' : 'Crear'}</button>
                          </div>
                        </form>
                      ) : (
                        <button onClick={() => setShowNewProject(true)} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5"><Plus className="w-4 h-4" /><span className="text-sm">Nuevo proyecto</span></button>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl lg:text-5xl font-bold text-white text-center mb-10">¿Qué construirás hoy?</motion.h1>

          {/* Quick Projects */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {projects.slice(0, 3).map((project) => (
              <button key={project.id} onClick={() => setSelectedProject(project)} className={`flex items-center gap-2 px-4 py-2 rounded-full border ${selectedProject?.id === project.id ? 'bg-white/10 border-[#FF6B00]/50 text-white' : 'bg-white/5 border-white/10 text-gray-400 hover:text-white'}`}>
                <div className={`w-2 h-2 rounded-full ${getProjectColor(project.type)}`} /><span className="text-sm">{project.name}</span>
              </button>
            ))}
            <button onClick={handleSurprise} className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 text-purple-300 hover:text-white" data-testid="surprise-btn">
              <Sparkles className="w-4 h-4" /><span className="text-sm">Sorpréndeme</span>
            </button>
          </div>

          {/* Chat */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto mb-8">
            <ChatInterface 
              project={selectedProject} 
              onSendMessage={handleSendMessage} 
              sending={sendingPrompt} 
              disabled={!selectedProject} 
              initialPrompt={templatePrompt || surprisePrompt} 
              onPromptUsed={() => { setSurprisePrompt(''); setTemplatePrompt(''); }} 
            />
            
            {/* Deploy button */}
            {selectedProject && (
              <div className="flex justify-end mt-4 gap-3">
                <button
                  onClick={() => setShowIDE(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 text-sm font-medium"
                  data-testid="open-ide-btn"
                >
                  <Code className="w-4 h-4" />
                  Abrir Editor
                </button>
                <button
                  onClick={() => setShowDeployModal(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 text-sm font-medium"
                  data-testid="deploy-project-btn"
                >
                  <Rocket className="w-4 h-4" />
                  Desplegar proyecto
                </button>
              </div>
            )}
          </motion.div>

          {/* Tabs */}
          <div className="flex justify-center gap-4 mb-8 text-sm flex-wrap">
            <button onClick={() => setActiveTab('tasks')} className={`flex items-center gap-2 pb-2 border-b-2 ${activeTab === 'tasks' ? 'text-white border-[#FF6B00]' : 'text-gray-500 border-transparent hover:text-white'}`}><Clock className="w-4 h-4" />Tareas</button>
            <span className="text-gray-700">|</span>
            <button onClick={() => setActiveTab('deployed')} className={`flex items-center gap-2 pb-2 border-b-2 ${activeTab === 'deployed' ? 'text-white border-[#FF6B00]' : 'text-gray-500 border-transparent hover:text-white'}`}><Rocket className="w-4 h-4" />Apps desplegadas</button>
            <span className="text-gray-700">|</span>
            <button onClick={() => setActiveTab('monitoring')} className={`flex items-center gap-2 pb-2 border-b-2 ${activeTab === 'monitoring' ? 'text-white border-[#FF6B00]' : 'text-gray-500 border-transparent hover:text-white'}`}><Activity className="w-4 h-4" />Monitoring</button>
            <span className="text-gray-700">|</span>
            <button onClick={() => setActiveTab('credits')} className={`flex items-center gap-2 pb-2 border-b-2 ${activeTab === 'credits' ? 'text-white border-[#FF6B00]' : 'text-gray-500 border-transparent hover:text-white'}`}><Coins className="w-4 h-4" />Créditos</button>
            <span className="text-gray-700">|</span>
            <button onClick={() => setActiveTab('referrals')} className={`flex items-center gap-2 pb-2 border-b-2 ${activeTab === 'referrals' ? 'text-white border-[#FF6B00]' : 'text-gray-500 border-transparent hover:text-white'}`}><Gift className="w-4 h-4" />Referidos</button>
            <span className="text-gray-700">|</span>
            <button onClick={() => setActiveTab('leaderboard')} className={`flex items-center gap-2 pb-2 border-b-2 ${activeTab === 'leaderboard' ? 'text-white border-[#FF6B00]' : 'text-gray-500 border-transparent hover:text-white'}`}><Trophy className="w-4 h-4" />Leaderboard</button>
          </div>

          {/* Content */}
          {activeTab === 'deployed' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-5xl mx-auto">
              <DeployedApps onDeploy={() => setShowDeployModal(true)} />
            </motion.div>
          )}
          
          {activeTab === 'tasks' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-4xl mx-auto">
              <div className="glass rounded-2xl overflow-hidden">
                <table className="w-full">
                  <thead><tr className="border-b border-white/10"><th className="text-left text-xs text-gray-500 uppercase px-6 py-4">ID</th><th className="text-left text-xs text-gray-500 uppercase px-6 py-4">Tarea</th><th className="text-left text-xs text-gray-500 uppercase px-6 py-4">Modelo</th><th className="text-left text-xs text-gray-500 uppercase px-6 py-4">Fecha</th></tr></thead>
                  <tbody>
                    {recentTasks.length === 0 ? (<tr><td colSpan={4} className="text-center py-12 text-gray-500"><Clock className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>Sin tareas</p></td></tr>) : (
                      recentTasks.map((task) => (
                        <tr key={task.id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="px-6 py-4"><span className="text-sm text-gray-500 font-mono">CT-{task.id.slice(0, 6)}</span></td>
                          <td className="px-6 py-4"><p className="text-white line-clamp-1">{task.prompt}</p></td>
                          <td className="px-6 py-4"><span className="px-2 py-1 rounded-lg bg-purple-500/20 text-purple-400 text-xs">{task.model || 'claude'}</span></td>
                          <td className="px-6 py-4 text-sm text-gray-500">{new Date(task.created_at).toLocaleDateString('es')}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}
          {activeTab === 'referrals' && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-xl mx-auto"><ReferralCard /></motion.div>}
          {activeTab === 'leaderboard' && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-xl mx-auto"><ReferralLeaderboard /></motion.div>}
          {activeTab === 'credits' && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-md mx-auto"><CreditsWidget /></motion.div>}
          {activeTab === 'monitoring' && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-5xl mx-auto"><MonitoringPanel /></motion.div>}
        </div>
      </main>
      <Footer />
      
      {/* Deploy Modal */}
      <DeployModal
        isOpen={showDeployModal}
        onClose={() => setShowDeployModal(false)}
        project={selectedProject}
        onSuccess={() => setActiveTab('deployed')}
      />

      {/* Project IDE */}
      {showIDE && selectedProject && (
        <ProjectIDE
          project={selectedProject}
          onClose={() => setShowIDE(false)}
        />
      )}
    </div>
  );
}
